import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  MenuItem,
  Typography,
  Alert,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { MATCH_DURATION_OPTIONS } from '@/utils/liveClock';

export interface LiveSessionFormValues {
  competition_name: string;
  home_team: string;
  away_team: string;
  match_date: string;
  notes: string;
  match_duration_minutes: number;
}

interface Props {
  open: boolean;
  title: string;
  initial?: Partial<LiveSessionFormValues>;
  error?: string | null;
  onClose: () => void;
  onSubmit: (values: LiveSessionFormValues) => void;
}

function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const empty = (): LiveSessionFormValues => ({
  competition_name: '',
  home_team: '',
  away_team: '',
  match_date: todayIsoDate(),
  notes: '',
  match_duration_minutes: 30,
});

export default function LiveSessionFormDialog({ open, title, initial, error, onClose, onSubmit }: Props) {
  const [values, setValues] = useState<LiveSessionFormValues>(empty());

  useEffect(() => {
    if (open) {
      setValues({ ...empty(), ...initial, match_date: initial?.match_date || todayIsoDate() });
    }
  }, [open, initial]);

  const handleChange = (field: keyof LiveSessionFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const value = field === 'match_duration_minutes' ? Number(raw) : raw;
    setValues((v) => ({ ...v, [field]: value }));
  };

  const canSave = values.home_team.trim().length > 0 && values.away_team.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="賽事名稱"
            value={values.competition_name}
            onChange={handleChange('competition_name')}
            fullWidth
            placeholder="例：甲組聯賽"
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="主隊 *"
              value={values.home_team}
              onChange={handleChange('home_team')}
              fullWidth
              required
            />
            <TextField
              label="客隊 *"
              value={values.away_team}
              onChange={handleChange('away_team')}
              fullWidth
              required
            />
          </Stack>
          <TextField
            select
            label="全場比賽時間"
            value={values.match_duration_minutes}
            onChange={handleChange('match_duration_minutes')}
            fullWidth
            helperText="計時器向上累計；中場休息為全場時間的一半；每段到達上限自動暫停。"
          >
            {MATCH_DURATION_OPTIONS.map((m) => (
              <MenuItem key={m} value={m}>
                {m} 分鐘
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="比賽日期"
            type="date"
            value={values.match_date}
            onChange={handleChange('match_date')}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField label="備註" value={values.notes} onChange={handleChange('notes')} fullWidth multiline rows={2} />
          <Typography variant="caption" color="text.secondary">
            加時每半場 5 分鐘；如需七米球決勝，請在加時後切換至「七米球決勝」時段。
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" color="secondary" disabled={!canSave} onClick={() => onSubmit(values)}>
          開始設定
        </Button>
      </DialogActions>
    </Dialog>
  );
}
